import { useMemo } from 'react';

import { YAxis, CartesianGrid, ResponsiveContainer, ComposedChart, ReferenceLine, Legend } from 'recharts';

import { GraphLineNode } from '@data';
import { useTranslate } from '@i18n';

import { colors, graphPalette } from '../../colors';
import { typography } from '../../typography';

import { type GraphProps } from './Graph';
import * as css from './Graph.css';
import { renderXAxisFloor, renderFloorLegend, renderCorridors, renderLines } from './renderers/';

import type { LegendPayload } from './Graph.types';

const graphMargin = { top: 32, bottom: 24, left: 32, right: 20 };

type FloorGraphNodes = GraphProps & {
    omitLines?: GraphLineNode[];
};

export function FloorGraph({
    data: { dataPoints, apex, transition, isQuickAnalysis, displaySymbol, corridorDataPoints },
    lines,
    segmentation,
    showComparison,
    omitLines,
}: FloorGraphNodes) {
    const colorMap = useMemo(
        () => (lines ? Object.fromEntries(lines?.map((d, i) => [d.id, Object.values(graphPalette)[i]])) : {}),
        [lines],
    );
    const t = useTranslate();

    if (!Array.isArray(dataPoints) || !dataPoints.length) {
        return (
            <div className={css.rootFloor}>
                <div className={css.fallback}>
                    <span
                        className={typography({
                            variant: 'h2',
                        })}
                    >
                        {t('error_message.no_data_available')}
                    </span>
                </div>
            </div>
        );
    }

    const hasApex = Object.keys(apex).length > 0;
    const hasTransition = Object.keys(transition).length > 0;

    const shouldHide = hasApex || hasTransition;

    return (
        <div className={css.rootFloor}>
            <ResponsiveContainer>
                <ComposedChart data={dataPoints} height={400} defaultShowTooltip={false} margin={graphMargin}>
                    <YAxis
                        key={displaySymbol}
                        yAxisId={displaySymbol}
                        orientation="left"
                        interval="equidistantPreserveStart"
                        width={28}
                        dx={-10 / 2}
                        fill={colors.white}
                        stroke={colors.white}
                        fontSize={18}
                        fontWeight={700}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(data) => `${data}${displaySymbol}`}
                    />

                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    {renderXAxisFloor({
                        isQuickAnalysis,
                        segmentation,
                        axisKey: displaySymbol ?? 'm',
                    })}
                    <ReferenceLine yAxisId={displaySymbol} y={0} stroke={colors.bluegray[400]} />
                    {renderCorridors({ corridorDataPoints, displaySymbol, colorMap })}
                    {renderLines({
                        lines,
                        displaySymbol,
                        apex,
                        transition,
                        colorMap,
                        showComparisonData: !!showComparison,
                        shouldHideComparison: shouldHide,
                        translate: t,
                    })}

                    <Legend
                        wrapperStyle={{
                            bottom: 4,
                            left: 0,
                            right: 0,
                            margin: 0,
                            width: `calc(100% - ${graphMargin.right}px)`,
                        }}
                        content={({ payload }) =>
                            payload && renderFloorLegend(payload as LegendPayload[], omitLines, t)
                        }
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}
